import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNow, format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

// Merge Tailwind classes
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Date d'une adresse comme /week/2026-10-19, lue en heure locale. Absente ou illisible,
 * c'est aujourd'hui.
 */
export function dateDepuisUrl(parametre: string | undefined): Date {
    const date = parametre ? parseISO(parametre) : null;
    return date && isValid(date) ? date : new Date();
}

// Format date relative (e.g., "il y a 2 heures")
export function formatRelativeDate(date: string | Date | null): string {
    if (!date) return '-';
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: fr });
}

// Format date (e.g., "06/01/2026 08:15")
export function formatDate(date: string | Date | null): string {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr });
}

// Format date short (e.g., "06/01/2026")
export function formatDateShort(date: string | Date | null): string {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy', { locale: fr });
}

// Format day name (e.g., "Lundi")
export function formatDayName(date: Date): string {
    return format(date, 'EEEE', { locale: fr });
}

// Format day short (e.g., "Lun.")
export function formatDayShort(date: Date): string {
    return format(date, 'EEE', { locale: fr });
}

// Format date for display (e.g., "Lundi 6")
export function formatDayDisplay(date: Date): string {
    return format(date, 'EEEE d', { locale: fr });
}

// Format month year (e.g., "Janvier 2026")
export function formatMonthYear(date: Date): string {
    return format(date, 'MMMM yyyy', { locale: fr });
}

// Get week days
export function getWeekDays(date: Date): Date[] {
    const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday
    const end = endOfWeek(date, { weekStartsOn: 1 }); // Sunday
    return eachDayOfInterval({ start, end });
}

// Check if same day
export function isSameDayFn(date1: Date, date2: Date): boolean {
    return isSameDay(date1, date2);
}

// Nombre d'heures par jour de travail (configurable)
const HOURS_PER_DAY = 8;

// Convertir des heures en jours
export function hoursToDays(hours: number): number {
    return Math.round((hours / HOURS_PER_DAY) * 100) / 100;
}

// Convertir des jours en heures
export function daysToHours(days: number): number {
    return Math.round(days * HOURS_PER_DAY * 100) / 100;
}

// Format days (e.g., "1j", "0.5j") - prend des HEURES et affiche en JOURS
export function formatDays(hours: number): string {
    const days = hoursToDays(hours);
    return `${days}j`;
}

// Alias for backward compatibility
export function formatHours(hours: number): string {
    return formatDays(hours);
}

// Format number with spaces (French format)
export function formatNumber(num: number): string {
    return num.toLocaleString('fr-FR');
}

// Calculate percentage
export function percentage(value: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
}

// Get status badge class
export function getStatusBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
        case 'draft':
        case 'brouillon':
            return 'badge-draft';
        case 'submitted':
        case 'soumis':
            return 'badge-submitted';
        case 'approved':
        case 'approuvé':
            return 'badge-approved';
        case 'rejected':
        case 'rejeté':
            return 'badge-rejected';
        case 'billed':
        case 'facturé':
            return 'badge-billed';
        default:
            return 'badge-draft';
    }
}

// Get status label in French
export function getStatusLabel(status: string): string {
    switch (status.toLowerCase()) {
        case 'draft':
            return 'Brouillon';
        case 'submitted':
            return 'Soumis';
        case 'approved':
            return 'Approuvé';
        case 'rejected':
            return 'Rejeté';
        case 'billed':
            return 'Facturé';
        default:
            return status;
    }
}

/**
 * Découpe la liste des types de service retenus, comme le fait le serveur : virgule,
 * point-virgule ou saut de ligne, sans espaces ni doublons. L'ordre de saisie est
 * conservé, c'est celui que l'interface réaffiche.
 */
export function parseServiceTypeValues(raw: string | null | undefined): string[] {
    const seen = new Set<string>();
    const values: string[] = [];

    for (const part of String(raw || '').split(/[,;\n]/)) {
        const value = part.trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        values.push(value);
    }

    return values;
}

/** Ajoute ou retire une valeur de la liste, en conservant l'ordre des autres. */
export function toggleServiceTypeValue(current: string[], value: string): string[] {
    return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}
